import { onBeforeUnmount, ref, unref, watch } from 'vue';

const FPS = 30, TOTAL_FRAMES = 30, SMALL_DIFF = 20;

/**
 * Animated counter shared by the total cards.
 *
 * Two guard variants exist in the original components and both are preserved:
 *  - A (default): the FIRST actual change snaps (no animation), later changes
 *    animate from the watcher's `oldCount`. Used by TotalCard / TotalCardWithSelect.
 *  - B (`{ skipFirstChange: false, from: 'display' }`): every change animates,
 *    starting from the currently displayed value (so a change mid-animation
 *    continues from where the number visually is). Used by TotalCardWithSwitch.
 *
 * `nullAsZero` (opt-in, default false — the three cards above leave it off, so their
 * path is unchanged): a null/undefined count DISPLAYS 0 (without counting as the first
 * change), and an animation that starts from a null/undefined old count starts from 0 —
 * the spark cards' own counter (issue-analysis, IA_ABSORPTION_PLAN Phase 4 step 13).
 * Without it a nullish count is shown as is (an empty number).
 *
 * @param {import('vue').Ref<number>|import('vue').ComputedRef<number>} countRef
 * @param {{ skipFirstChange?: boolean, from?: 'old'|'display', nullAsZero?: boolean }} [options]
 */
export function useAnimatedCount(countRef, { skipFirstChange = true, from = 'old', nullAsZero = false } = {}) {
  const displayCount = ref(unref(countRef));
  let intervalId = null, firstChangeCompleted = false;

  const animateCount = (start, end) => {
    if (nullAsZero && (start === undefined || start === null)) start = 0;
    if (intervalId) clearInterval(intervalId);
    const difference = end - start;
    let step = Math.abs(difference) < SMALL_DIFF
      ? Math.max(1, Math.abs(Math.ceil(difference / TOTAL_FRAMES)))
      : Math.max(1, Math.abs(Math.ceil(difference / SMALL_DIFF)));
    if (difference < 0) step = -step;
    let current = start;
    displayCount.value = current;
    intervalId = setInterval(() => {
      current += step;
      if ((step > 0 && current >= end) || (step < 0 && current <= end)) {
        displayCount.value = end; clearInterval(intervalId); intervalId = null;
      } else displayCount.value = current;
    }, 1000 / FPS);
  };

  watch(() => unref(countRef), (newCount, oldCount) => {
    if (nullAsZero && (newCount === undefined || newCount === null)) { displayCount.value = 0; return; }
    if (oldCount === undefined) { displayCount.value = newCount; return; }
    if (newCount === oldCount) return;
    if (skipFirstChange && !firstChangeCompleted) { displayCount.value = newCount; firstChangeCompleted = true; return; }
    animateCount(from === 'display' ? displayCount.value : oldCount, newCount);
  }, { immediate: true });

  onBeforeUnmount(() => { if (intervalId) clearInterval(intervalId); });   // originals leak on unmount

  return { displayCount };
}
